// Lazy loader around the AI glue code. The actual `removeBackground` function
// (and its bare `onnxruntime-web` import) is NOT vendored raw — a native
// browser import() cannot resolve a bare specifier, so it's esbuild-bundled
// ahead of time into a separate lazy entry (app/javascript/background_removal_lib.js
// -> app/assets/builds/background_removal_lib.js) and loaded here via its
// digested asset URL, passed in as `moduleUrl` by the caller (the controller
// reads it off the modal's data attribute, rendered server-side via
// asset_path so it works with Propshaft digesting in production).
//
// The ~40 MB of model/wasm resources are a separate concern: they're served
// same-origin from public/vendor (see scripts/copy_background_removal_assets.mjs)
// and fetched by the library itself at runtime via `publicPath`, only when a
// user first clicks Remove Background.
//
// The import is routed through an indirection (a plain variable holding the
// URL) so esbuild cannot constant-fold `import(moduleUrl)` into a bundled,
// build-time-resolved specifier — it must stay a genuine runtime import().

export class BackgroundRemovalError extends Error {
  constructor(message, userMessage) {
    super(message)
    this.name = "BackgroundRemovalError"
    this.userMessage = userMessage
  }
}

const UNAVAILABLE_MESSAGE = "Background removal is unavailable right now."

let modulePromise = null

function vendorBase() {
  return new URL("/vendor/background-removal/", window.location.origin).href
}

function defaultLoader(moduleUrl) {
  const url = moduleUrl // indirection: keeps esbuild from constant-folding this import()
  return import(url)
}

export function resetBackgroundRemovalCache() {
  modulePromise = null
}

export async function removeBackgroundFrom(file, { onProgress, loader, moduleUrl } = {}) {
  if (!loader && !moduleUrl) {
    throw new BackgroundRemovalError("moduleUrl is required when no loader is provided", UNAVAILABLE_MESSAGE)
  }

  if (!modulePromise) modulePromise = (loader || defaultLoader)(moduleUrl)
  let mod
  try {
    mod = await modulePromise
  } catch (e) {
    modulePromise = null // a transient network failure must not poison future attempts
    throw new BackgroundRemovalError(`module load failed: ${e.message}`, UNAVAILABLE_MESSAGE)
  }
  try {
    return await mod.removeBackground(file, {
      publicPath: vendorBase(),
      progress: (_key, current, total) => {
        if (onProgress && total > 0) onProgress(current / total)
      },
    })
  } catch (e) {
    throw new BackgroundRemovalError(`processing failed: ${e.message}`, UNAVAILABLE_MESSAGE)
  }
}
